import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import type {TFunction} from 'i18next'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {usePagedList} from '@/hooks/common/use-paged-list'
import {track} from '@/lib/telemetry'
import {translateApiError} from '@/lib/errors'
import type {components} from '@/lib/api-schema'

export type EvidenceList = components['schemas']['EvidenceListRead']
export type Evidence = components['schemas']['EvidenceRead']
export type EmployeeEvidenceYear = components['schemas']['EmployeeEvidenceYearRead']
type EvidenceDescarga = components['schemas']['EvidenceDescargaRead']
export type UploadTarget = components['schemas']['UploadTargetRead']
export type ValidationFinding = components['schemas']['ValidationFindingRead']

export function useEvidence(businessId: string | undefined, year?: number) {
  return useQuery({
    queryKey: ['evidence', businessId, year ?? 'current'],
    queryFn: async () =>
      (
        await api.get<EvidenceList>(`/businesses/${businessId}/sgsst/evidence`, {
          params: year ? {year} : undefined,
        })
      ).data,
    enabled: !!businessId,
  })
}

const EVIDENCIA_PAGE_SIZE = 10

export function useEmployeeEvidence(businessId: string | undefined, employeeId: string | null, page = 1) {
  return usePagedList<EmployeeEvidenceYear>(
    ['evidence', businessId, 'employee', employeeId],
    businessId && employeeId ? `/businesses/${businessId}/employees/${employeeId}/evidence` : undefined,
    {page, search: ''},
    EVIDENCIA_PAGE_SIZE
  )
}

export function rutaDeDescarga(businessId: string, evidenceId: string): string {
  return `/businesses/${businessId}/sgsst/evidence/${evidenceId}/descarga`
}

// Toda URL que entrega el backend vale al menos 5 min más (`SIGNED_URL_CACHE_TTL_SECONDS`).
const URL_VIGENTE_MS = 4 * 60_000

function urlFirmada(ruta: string) {
  return {
    queryKey: ['evidencia-descarga', ruta],
    queryFn: async () => (await api.get<EvidenceDescarga>(ruta)).data.download_url,
    staleTime: URL_VIGENTE_MS,
    gcTime: URL_VIGENTE_MS,
  }
}

/** Una firma por archivo y solo cuando se va a ver: la lista no trae URLs (KAI-445). */
export function useUrlDeEvidencia(ruta: string, visible: boolean) {
  return useQuery({...urlFirmada(ruta), enabled: visible})
}

type Apertura = {ruta: string; pestana: Window | null}

/** La pestaña se abre durante el clic: tras la espera de la firma, el bloqueador de ventanas la impediría.
 * Cada clic pasa por el servidor aunque la URL siga vigente: abrir un documento de salud queda registrado. */
export function useAbrirEvidencia() {
  const queryClient = useQueryClient()
  const firma = useMutation({
    mutationFn: ({ruta}: Apertura) => queryClient.fetchQuery({...urlFirmada(ruta), staleTime: 0}),
    onSuccess: (url, {pestana}) => {
      if (pestana) pestana.location.href = url
      else window.location.assign(url)
    },
    onError: (_error, {pestana}) => pestana?.close(),
  })
  const abrir = (ruta: string) => {
    const pestana = window.open('', '_blank')
    if (pestana) pestana.opener = null
    firma.mutate({ruta, pestana})
  }
  return {abrir, isPending: firma.isPending}
}

export async function mintUploadTarget(businessId: string, file: File): Promise<UploadTarget> {
  return (
    await api.post<UploadTarget>(`/businesses/${businessId}/sgsst/evidence-uploads`, {
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
    })
  ).data
}

/** Los pone el navegador: `setRequestHeader` concatena y repetir `Content-Type` rompe la firma
 * V4; `Host` y `Content-Length` son forbidden header names. Lista negra a propósito. */
const LOS_PONE_EL_NAVEGADOR = new Set(['content-type', 'host', 'content-length'])

/** XHR porque fetch no da progreso de subida. `target.headers` van firmados en la URL: deben
 * viajar tal cual o la firma deja de coincidir. */
export function uploadToStorage(
  // Structural, not `UploadTarget`: the licencia/logo/scan mints are nominally distinct
  // OpenAPI schemas with the same shape, and all of them come through here.
  target: {url: string; method: string; headers?: Record<string, string>},
  file: File,
  onProgress: (pct: number) => void,
  contentType?: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(target.method, target.url)
    xhr.setRequestHeader('Content-Type', contentType ?? file.type)
    for (const [name, value] of Object.entries(target.headers ?? {})) {
      if (!LOS_PONE_EL_NAVEGADOR.has(name.toLowerCase())) xhr.setRequestHeader(name, value)
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
    }
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`upload failed (${xhr.status})`))
    xhr.onerror = () => reject(new Error('upload failed'))
    xhr.send(file)
  })
}

function useEvidenceInvalidation(businessId: string) {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({queryKey: ['evidence', businessId]})
}

export function useRegisterEvidence(businessId: string) {
  const invalidate = useEvidenceInvalidation(businessId)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      standard_code: string
      object_key: string
      filename: string
      content_type: string
      size_bytes: number
      employee_ids: string[]
      // Omitido = sin segunda lectura de IA; el registro no la espera.
      validate_ai?: boolean
      soporte_key?: string | null
      // Its variante (1.1.4: which entity's certificate); omitted = 'file'.
      kind?: string
      vence_el?: string | null
      // Mes cubierto de una casilla `cadencia_mensual` (obligatorio ahí); el año lo deriva el
      // backend del periodo abierto.
      periodo_mes?: number | null
    }) => api.post<EvidenceList>(`/businesses/${businessId}/sgsst/evidence`, input),
    onSuccess: () => {
      invalidate()
      // The declared casilla moves the per-casilla states on the checklist read.
      queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
    },
  })
}

export function useEditEvidenceSoporte(businessId: string) {
  const invalidate = useEvidenceInvalidation(businessId)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      evidenceId: string
      soporte_key: string | null
      kind?: string
      vence_el: string | null
      periodo_mes?: number | null
    }) =>
      api.patch(`/businesses/${businessId}/sgsst/evidence/${input.evidenceId}`, {
        soporte_key: input.soporte_key,
        kind: input.kind ?? null,
        vence_el: input.vence_el,
        periodo_mes: input.periodo_mes ?? null,
      }),
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
      queryClient.invalidateQueries({queryKey: ['soportes-vencimientos', businessId]})
    },
  })
}

/** Espejo de `add_months` del backend (31 ene + 1 mes = 28/29 feb): el prefill de «Vence el» es
 * lo que el servidor guardaría. */
export function addMonthsIso(from: Date, months: number): string {
  const target = new Date(from.getFullYear(), from.getMonth() + months, 1)
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  target.setDate(Math.min(from.getDate(), lastDay))
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}`
}

/** Espejo de VENCE_PRONTO_DIAS = 30 (`backend/app/common/soportes.py`): sin segunda petición. */
export function expiryState(venceEl: string | null | undefined, today = new Date()): 'vencido' | 'vence_pronto' | null {
  if (!venceEl) return null
  const expiry = new Date(`${venceEl}T00:00:00`)
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const days = Math.round((expiry.getTime() - start.getTime()) / 86_400_000)
  if (days < 0) return 'vencido'
  if (days <= 30) return 'vence_pronto'
  return null
}

// Silencia el aviso de IA sin tocar el archivo: cobertura y preparación no se mueven.
export function useDismissValidation(businessId: string) {
  const invalidate = useEvidenceInvalidation(businessId)
  return useMutation({
    mutationFn: async (evidenceId: string) =>
      api.post(`/businesses/${businessId}/sgsst/evidence/${evidenceId}/validation/dismiss`),
    onSuccess: () => {
      invalidate()
      track('ai_dismissed', {business_id: businessId})
    },
  })
}

export function useReanalyze(businessId: string) {
  const invalidate = useEvidenceInvalidation(businessId)
  return useMutation({
    mutationFn: async (evidenceId: string) =>
      api.post(`/businesses/${businessId}/sgsst/evidence/${evidenceId}/validation/reanalyze`),
    onSuccess: invalidate,
  })
}

export function useRemoveEvidence(businessId: string) {
  const invalidate = useEvidenceInvalidation(businessId)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (evidenceId: string) => api.delete(`/businesses/${businessId}/sgsst/evidence/${evidenceId}`),
    onSuccess: () => {
      invalidate()
      // Removing an aportado file reopens its solicitud (pendiente/vencida again).
      queryClient.invalidateQueries({queryKey: ['solicitudes', businessId]})
      queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
      queryClient.invalidateQueries({queryKey: ['soportes-vencimientos', businessId]})
    },
  })
}

export function useConfirmEvidence(businessId: string) {
  const invalidate = useEvidenceInvalidation(businessId)
  return useMutation({
    mutationFn: async (evidenceId: string) =>
      api.post(`/businesses/${businessId}/sgsst/evidence/${evidenceId}/confirm`),
    onSuccess: invalidate,
  })
}

export const EVIDENCE_ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf,.pdf,.jpg,.jpeg,.png,.webp,.xlsx,.docx'

// Espejo de `MAX_EVIDENCE_BYTES` del backend (KAI-157); el registro y la URL firmada rechazan igual.
export const EVIDENCE_MAX_BYTES = 50 * 1024 * 1024

function megas(bytes: number): string {
  return (bytes / (1024 * 1024)).toLocaleString('es-CO', {maximumFractionDigits: 1})
}

export function evidenceTooBig(t: TFunction, file: {size: number}): string {
  return t('evidence.tooBig', {tamano: megas(file.size), max: megas(EVIDENCE_MAX_BYTES)})
}

// Espejo de la lista blanca del backend (formatos sin macros).
export const EVIDENCE_ALLOWED_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
])

export function evidenceKindLabel(contentType: string): 'img' | 'pdf' | 'xlsx' | 'docx' {
  if (contentType.startsWith('image/')) return 'img'
  if (contentType === 'application/pdf') return 'pdf'
  if (contentType.endsWith('spreadsheetml.sheet')) return 'xlsx'
  return 'docx'
}

// Espejo de `is_ai_analyzable` del backend: solo imágenes raster y PDF llegan al modelo.
export function isAiAnalyzable(contentType: string): boolean {
  return contentType.startsWith('image/') || contentType === 'application/pdf'
}

export function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toLocaleString('es-CO', {maximumFractionDigits: 1})} MB`
  return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString('es-CO')} KB`
}

export function useEvidenceUploadFlow(upload: (file: File, onProgress: (pct: number) => void) => Promise<void>) {
  const {t} = useTranslation()
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function start(files: FileList | null) {
    const file = files?.[0]
    if (!file) return
    setError(null)
    if (!EVIDENCE_ALLOWED_TYPES.has(file.type)) {
      setError(t('errors.evidenceBadFormat'))
      return
    }
    if (file.size > EVIDENCE_MAX_BYTES) {
      setError(evidenceTooBig(t, file))
      return
    }
    try {
      setProgress(0)
      await upload(file, setProgress)
      setProgress(null)
    } catch (err) {
      setProgress(null)
      setError(translateApiError(err))
    }
  }

  return {start, progress, error}
}
